import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import { useSEO } from '../hooks/useSEO';
import {
  twoFactorApi,
  platformContactsApi,
  type TwoFAStatusResponse,
  type TwoFASetupResponse,
  type PlatformContactInfo,
  type PlatformName,
  PLATFORM_DISPLAY_NAMES,
} from '../services/api';
import styles from './SettingsPage.module.css';

type TwoFAStep = 'status' | 'setup' | 'verify' | 'backup-codes' | 'disable';

const PLATFORMS: PlatformName[] = ['trendyol', 'hepsiburada', 'n11', 'amazon'];

export function SettingsPage() {
  const { token } = useAuth();
  const { language } = useLanguage();

  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState<TwoFAStatusResponse | null>(null);
  const [twoFASetup, setTwoFASetup] = useState<TwoFASetupResponse | null>(null);
  const [twoFAStep, setTwoFAStep] = useState<TwoFAStep>('status');
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Platform Contacts state
  const [platformContacts, setPlatformContacts] = useState<Record<string, PlatformContactInfo>>({});
  const [editingPlatform, setEditingPlatform] = useState<PlatformName | null>(null);
  const [platformEmail, setPlatformEmail] = useState('');
  const [platformPhone, setPlatformPhone] = useState('');
  const [verificationPlatform, setVerificationPlatform] = useState<PlatformName | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState('');
  const [platformSuccess, setPlatformSuccess] = useState('');

  useSEO({
    title: language === 'tr' ? 'Ayarlar' : 'Settings',
    description: language === 'tr' ? 'Hesap ayarları ve güvenlik' : 'Account settings and security',
  });

  // Load 2FA status and platform contacts on mount
  useEffect(() => {
    if (token) {
      loadTwoFAStatus();
      loadPlatformContacts();
    }
  }, [token]);

  const loadPlatformContacts = async () => {
    if (!token) return;
    try {
      const response = await platformContactsApi.getContacts(token);
      if (response.data) {
        setPlatformContacts(response.data.contacts);
      }
    } catch (err) {
      console.error('Failed to load platform contacts:', err);
    }
  };

  const loadTwoFAStatus = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await twoFactorApi.getStatus(token);
      if (response.data) {
        setTwoFAStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await twoFactorApi.setup(token);
      if (response.error) {
        setError(response.error);
        return;
      }
      if (response.data) {
        setTwoFASetup(response.data);
        setBackupCodes(response.data.backup_codes);
        setTwoFAStep('setup');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await twoFactorApi.verifySetup(token, verifyCode);
      if (response.error) {
        setError(response.error);
        return;
      }
      if (response.data) {
        setBackupCodes(response.data.backup_codes);
        setTwoFAStep('backup-codes');
        setSuccess(language === 'tr' ? '2FA başarıyla etkinleştirildi!' : '2FA enabled successfully!');
        loadTwoFAStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await twoFactorApi.disable(token, disablePassword, disableCode);
      if (response.error) {
        setError(response.error);
        return;
      }
      setSuccess(language === 'tr' ? '2FA devre dışı bırakıldı' : '2FA has been disabled');
      setTwoFAStep('status');
      setDisablePassword('');
      setDisableCode('');
      loadTwoFAStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await twoFactorApi.regenerateBackupCodes(token, verifyCode);
      if (response.error) {
        setError(response.error);
        return;
      }
      if (response.data) {
        setBackupCodes(response.data.backup_codes);
        setTwoFAStep('backup-codes');  // Show the new backup codes
        setSuccess(language === 'tr' ? 'Yedek kodlar yenilendi!' : 'Backup codes regenerated!');
        setVerifyCode('');
        loadTwoFAStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate codes');
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setSuccess(language === 'tr' ? 'Kodlar kopyalandı!' : 'Codes copied!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const downloadBackupCodes = () => {
    const codesText = `Polaris 2FA Backup Codes\n${'='.repeat(30)}\n\n${backupCodes.join('\n')}\n\n${'='.repeat(30)}\nKeep these codes safe. Each code can only be used once.`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'polaris-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Platform contact handlers
  const startEditingPlatform = (platform: PlatformName) => {
    const contact = platformContacts[platform];
    setEditingPlatform(platform);
    setPlatformEmail(contact?.email || '');
    setPlatformPhone(contact?.phone || '');
    setPlatformError('');
    setPlatformSuccess('');
    setVerificationPlatform(null);
    setVerificationCode('');
  };

  const cancelEditingPlatform = () => {
    setEditingPlatform(null);
    setPlatformEmail('');
    setPlatformPhone('');
    setPlatformError('');
    setVerificationPlatform(null);
    setVerificationCode('');
  };

  const handleUpdatePhone = async (platform: PlatformName) => {
    if (!token) return;
    setPlatformLoading(true);
    setPlatformError('');
    try {
      const response = await platformContactsApi.updateContact(token, platform, { phone: platformPhone });
      if (response.error) {
        setPlatformError(response.error);
        return;
      }
      setPlatformSuccess(language === 'tr' ? 'Telefon güncellendi!' : 'Phone updated!');
      loadPlatformContacts();
      setTimeout(() => setPlatformSuccess(''), 2000);
    } catch (err) {
      setPlatformError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleSendVerification = async (platform: PlatformName) => {
    if (!token || !platformEmail.trim()) return;
    setPlatformLoading(true);
    setPlatformError('');
    try {
      const response = await platformContactsApi.sendVerification(token, platform, platformEmail.trim());
      if (response.error) {
        setPlatformError(response.error);
        return;
      }
      setVerificationPlatform(platform);
      setPlatformSuccess(language === 'tr' ? 'Doğrulama kodu gönderildi!' : 'Verification code sent!');
      setTimeout(() => setPlatformSuccess(''), 3000);
    } catch (err) {
      setPlatformError(err instanceof Error ? err.message : 'Failed to send verification');
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!token || !verificationPlatform || !verificationCode.trim()) return;
    setPlatformLoading(true);
    setPlatformError('');
    try {
      const response = await platformContactsApi.verifyEmail(token, verificationPlatform, verificationCode.trim());
      if (response.error) {
        setPlatformError(response.error);
        return;
      }
      setPlatformSuccess(language === 'tr' ? 'E-posta doğrulandı!' : 'Email verified!');
      loadPlatformContacts();
      cancelEditingPlatform();
      setTimeout(() => setPlatformSuccess(''), 2000);
    } catch (err) {
      setPlatformError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleClearEmail = async (platform: PlatformName) => {
    console.log('[SettingsPage] handleClearEmail called for platform:', platform);
    if (!token) {
      console.log('[SettingsPage] No token, returning');
      return;
    }
    setPlatformLoading(true);
    setPlatformError('');
    try {
      // Use special marker "__CLEAR__" because Pydantic converts "" to None
      console.log('[SettingsPage] Calling API with __CLEAR__');
      const response = await platformContactsApi.updateContact(token, platform, { email: '__CLEAR__' });
      console.log('[SettingsPage] API response:', response);
      if (response.error) {
        console.log('[SettingsPage] API error:', response.error);
        setPlatformError(response.error);
        return;
      }
      console.log('[SettingsPage] Success, reloading contacts');
      setPlatformSuccess(language === 'tr' ? 'E-posta silindi!' : 'Email cleared!');
      await loadPlatformContacts();
      setPlatformEmail('');
      setTimeout(() => setPlatformSuccess(''), 2000);
    } catch (err) {
      console.error('[SettingsPage] Exception:', err);
      setPlatformError(err instanceof Error ? err.message : 'Failed to clear email');
    } finally {
      console.log('[SettingsPage] Finally block, setting loading to false');
      setPlatformLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>
        {language === 'tr' ? 'Ayarlar' : 'Settings'}
      </h1>

      {/* Security Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {language === 'tr' ? 'Güvenlik' : 'Security'}
        </h2>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitleRow}>
              <h3 className={styles.cardTitle}>
                {language === 'tr' ? 'İki Faktörlü Doğrulama (2FA)' : 'Two-Factor Authentication (2FA)'}
              </h3>
              {twoFAStatus?.enabled && (
                <span className={styles.enabledBadge}>
                  {language === 'tr' ? 'Etkin' : 'Enabled'}
                </span>
              )}
            </div>
            <p className={styles.cardDescription}>
              {language === 'tr'
                ? 'Google Authenticator veya benzeri bir uygulama ile hesabınızı koruyun.'
                : 'Protect your account with Google Authenticator or a similar app.'}
            </p>
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          {/* Status View - 2FA not enabled */}
          {twoFAStep === 'status' && !twoFAStatus?.enabled && (
            <div className={styles.cardContent}>
              <button
                onClick={handleSetup2FA}
                disabled={isLoading}
                className={styles.primaryBtn}
              >
                {isLoading
                  ? (language === 'tr' ? 'Yükleniyor...' : 'Loading...')
                  : (language === 'tr' ? '2FA Etkinleştir' : 'Enable 2FA')}
              </button>
            </div>
          )}

          {/* Status View - 2FA enabled */}
          {twoFAStep === 'status' && twoFAStatus?.enabled && (
            <div className={styles.cardContent}>
              <p className={styles.infoText}>
                {language === 'tr'
                  ? `Kalan yedek kod: ${twoFAStatus.backup_codes_remaining}`
                  : `Backup codes remaining: ${twoFAStatus.backup_codes_remaining}`}
              </p>
              <div className={styles.buttonGroup}>
                <button
                  onClick={() => setTwoFAStep('verify')}
                  className={styles.secondaryBtn}
                >
                  {language === 'tr' ? 'Yedek Kodları Yenile' : 'Regenerate Backup Codes'}
                </button>
                <button
                  onClick={() => setTwoFAStep('disable')}
                  className={styles.dangerBtn}
                >
                  {language === 'tr' ? '2FA Devre Dışı Bırak' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          )}

          {/* Setup Step - Show QR Code */}
          {twoFAStep === 'setup' && twoFASetup && (
            <div className={styles.cardContent}>
              <div className={styles.setupInstructions}>
                <p className={styles.stepNumber}>1.</p>
                <p>
                  {language === 'tr'
                    ? 'Google Authenticator veya benzeri bir uygulama indirin.'
                    : 'Download Google Authenticator or a similar app.'}
                </p>
              </div>

              <div className={styles.setupInstructions}>
                <p className={styles.stepNumber}>2.</p>
                <p>
                  {language === 'tr'
                    ? 'Aşağıdaki QR kodu tarayın veya gizli anahtarı manuel girin:'
                    : 'Scan the QR code below or enter the secret key manually:'}
                </p>
              </div>

              <div className={styles.qrContainer}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFASetup.qr_uri)}`}
                  alt="2FA QR Code"
                  className={styles.qrCode}
                />
                <div className={styles.secretKey}>
                  <label>{language === 'tr' ? 'Gizli Anahtar:' : 'Secret Key:'}</label>
                  <code>{twoFASetup.secret}</code>
                </div>
              </div>

              <div className={styles.setupInstructions}>
                <p className={styles.stepNumber}>3.</p>
                <p>
                  {language === 'tr'
                    ? 'Uygulamadaki 6 haneli kodu aşağıya girin:'
                    : 'Enter the 6-digit code from your app:'}
                </p>
              </div>

              <form onSubmit={handleVerifySetup} className={styles.verifyForm}>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={styles.codeInput}
                  maxLength={6}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={isLoading || verifyCode.length !== 6}
                  className={styles.primaryBtn}
                >
                  {isLoading
                    ? (language === 'tr' ? 'Doğrulanıyor...' : 'Verifying...')
                    : (language === 'tr' ? 'Doğrula ve Etkinleştir' : 'Verify & Enable')}
                </button>
              </form>

              <button
                onClick={() => {
                  setTwoFAStep('status');
                  setTwoFASetup(null);
                  setVerifyCode('');
                }}
                className={styles.cancelBtn}
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          )}

          {/* Backup Codes View */}
          {twoFAStep === 'backup-codes' && backupCodes.length > 0 && (
            <div className={styles.cardContent}>
              <div className={styles.warningBox}>
                <strong>{language === 'tr' ? 'Önemli!' : 'Important!'}</strong>
                <p>
                  {language === 'tr'
                    ? 'Bu kodları güvenli bir yere kaydedin. Her kod sadece bir kez kullanılabilir.'
                    : 'Save these codes in a safe place. Each code can only be used once.'}
                </p>
              </div>

              <div className={styles.backupCodesGrid}>
                {backupCodes.map((code, index) => (
                  <code key={index} className={styles.backupCode}>{code}</code>
                ))}
              </div>

              <div className={styles.buttonGroup}>
                <button onClick={copyBackupCodes} className={styles.secondaryBtn}>
                  {language === 'tr' ? 'Kopyala' : 'Copy'}
                </button>
                <button onClick={downloadBackupCodes} className={styles.secondaryBtn}>
                  {language === 'tr' ? 'İndir' : 'Download'}
                </button>
              </div>

              <button
                onClick={() => {
                  setTwoFAStep('status');
                  setBackupCodes([]);
                }}
                className={styles.primaryBtn}
              >
                {language === 'tr' ? 'Tamam' : 'Done'}
              </button>
            </div>
          )}

          {/* Regenerate Backup Codes - requires TOTP verification */}
          {twoFAStep === 'verify' && twoFAStatus?.enabled && (
            <div className={styles.cardContent}>
              <p className={styles.infoText}>
                {language === 'tr'
                  ? 'Yeni yedek kodlar oluşturmak için mevcut kodunuzu girin:'
                  : 'Enter your current code to generate new backup codes:'}
              </p>
              <form onSubmit={handleRegenerateBackupCodes} className={styles.verifyForm}>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={styles.codeInput}
                  maxLength={6}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={isLoading || verifyCode.length !== 6}
                  className={styles.primaryBtn}
                >
                  {isLoading
                    ? (language === 'tr' ? 'Yenileniyor...' : 'Regenerating...')
                    : (language === 'tr' ? 'Yedek Kodları Yenile' : 'Regenerate Codes')}
                </button>
              </form>
              <button
                onClick={() => {
                  setTwoFAStep('status');
                  setVerifyCode('');
                }}
                className={styles.cancelBtn}
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          )}

          {/* Disable 2FA */}
          {twoFAStep === 'disable' && (
            <div className={styles.cardContent}>
              <div className={styles.warningBox}>
                <strong>{language === 'tr' ? 'Uyarı!' : 'Warning!'}</strong>
                <p>
                  {language === 'tr'
                    ? '2FA devre dışı bırakıldığında hesabınız daha az güvenli olacaktır.'
                    : 'Disabling 2FA will make your account less secure.'}
                </p>
              </div>

              <form onSubmit={handleDisable2FA} className={styles.disableForm}>
                <div className={styles.field}>
                  <label>{language === 'tr' ? 'Şifreniz' : 'Your Password'}</label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label>{language === 'tr' ? '2FA Kodu veya Yedek Kod' : '2FA Code or Backup Code'}</label>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                    className={styles.input}
                    placeholder={language === 'tr' ? '6 haneli kod veya yedek kod' : '6-digit code or backup code'}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !disablePassword || !disableCode}
                  className={styles.dangerBtn}
                >
                  {isLoading
                    ? (language === 'tr' ? 'Devre Dışı Bırakılıyor...' : 'Disabling...')
                    : (language === 'tr' ? '2FA Devre Dışı Bırak' : 'Disable 2FA')}
                </button>
              </form>
              <button
                onClick={() => {
                  setTwoFAStep('status');
                  setDisablePassword('');
                  setDisableCode('');
                }}
                className={styles.cancelBtn}
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Platform Contacts Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {language === 'tr' ? 'Platform İletişim Bilgileri' : 'Platform Contact Information'}
        </h2>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <p className={styles.cardDescription}>
              {language === 'tr'
                ? 'Her platform için ayrı e-posta ve telefon numarası belirleyebilirsiniz. E-posta adresleri doğrulama gerektirir.'
                : 'Set different email and phone for each platform. Email addresses require verification.'}
            </p>
          </div>

          {platformError && <p className={styles.error}>{platformError}</p>}
          {platformSuccess && <p className={styles.success}>{platformSuccess}</p>}

          <div className={styles.cardContent}>
            {PLATFORMS.map((platform) => {
              const contact = platformContacts[platform];
              const isEditing = editingPlatform === platform;
              const isVerifying = verificationPlatform === platform;

              return (
                <div key={platform} className={styles.platformCard}>
                  <div className={styles.platformHeader}>
                    <h4 className={styles.platformName}>{PLATFORM_DISPLAY_NAMES[platform]}</h4>
                    {!isEditing && (
                      <button
                        onClick={() => startEditingPlatform(platform)}
                        className={styles.editBtn}
                      >
                        {language === 'tr' ? 'Düzenle' : 'Edit'}
                      </button>
                    )}
                  </div>

                  {!isEditing ? (
                    <div className={styles.platformInfo}>
                      <div className={styles.contactRow}>
                        <span className={styles.contactLabel}>
                          {language === 'tr' ? 'E-posta:' : 'Email:'}
                        </span>
                        <span className={styles.contactValue}>
                          {contact?.email || (language === 'tr' ? '(ayarlanmadı)' : '(not set)')}
                          {contact?.email && contact?.email_verified && (
                            <span className={styles.verifiedBadge}>✓</span>
                          )}
                          {contact?.email && !contact?.email_verified && (
                            <span className={styles.unverifiedBadge}>
                              {language === 'tr' ? '(doğrulanmadı)' : '(unverified)'}
                            </span>
                          )}
                          {contact?.is_default && (
                            <span className={styles.defaultBadge}>
                              {language === 'tr' ? '(varsayılan)' : '(default)'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className={styles.contactRow}>
                        <span className={styles.contactLabel}>
                          {language === 'tr' ? 'Telefon:' : 'Phone:'}
                        </span>
                        <span className={styles.contactValue}>
                          {contact?.phone || (language === 'tr' ? '(ayarlanmadı)' : '(not set)')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.platformEditForm}>
                      {/* Email Field */}
                      <div className={styles.field}>
                        <label>{language === 'tr' ? 'E-posta' : 'Email'}</label>
                        <div className={styles.inputGroup}>
                          <input
                            type="email"
                            value={platformEmail}
                            onChange={(e) => setPlatformEmail(e.target.value)}
                            className={styles.input}
                            placeholder={language === 'tr' ? 'platform@ornek.com' : 'platform@example.com'}
                            disabled={isVerifying}
                          />
                          {!isVerifying && (
                            <button
                              onClick={() => handleSendVerification(platform)}
                              disabled={platformLoading || !platformEmail.trim()}
                              className={styles.secondaryBtn}
                            >
                              {platformLoading
                                ? '...'
                                : (language === 'tr' ? 'Doğrula' : 'Verify')}
                            </button>
                          )}
                          {contact?.email && !isVerifying && !contact?.is_default && (
                            <button
                              onClick={() => handleClearEmail(platform)}
                              disabled={platformLoading}
                              className={styles.dangerBtnSmall}
                              title={language === 'tr' ? 'E-postayı Sil' : 'Clear Email'}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Verification Code Input */}
                      {isVerifying && (
                        <div className={styles.field}>
                          <label>{language === 'tr' ? 'Doğrulama Kodu' : 'Verification Code'}</label>
                          <div className={styles.inputGroup}>
                            <input
                              type="text"
                              value={verificationCode}
                              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className={styles.input}
                              placeholder="000000"
                              maxLength={6}
                              autoFocus
                            />
                            <button
                              onClick={handleVerifyEmail}
                              disabled={platformLoading || verificationCode.length !== 6}
                              className={styles.primaryBtn}
                            >
                              {platformLoading
                                ? '...'
                                : (language === 'tr' ? 'Onayla' : 'Confirm')}
                            </button>
                          </div>
                          <p className={styles.helpText}>
                            {language === 'tr'
                              ? `E-postanıza gönderilen 6 haneli kodu girin (${platformEmail})`
                              : `Enter the 6-digit code sent to ${platformEmail}`}
                          </p>
                        </div>
                      )}

                      {/* Phone Field */}
                      <div className={styles.field}>
                        <label>{language === 'tr' ? 'Telefon' : 'Phone'}</label>
                        <div className={styles.inputGroup}>
                          <input
                            type="tel"
                            value={platformPhone}
                            onChange={(e) => setPlatformPhone(e.target.value)}
                            className={styles.input}
                            placeholder="+90 5XX XXX XX XX"
                          />
                          <button
                            onClick={() => handleUpdatePhone(platform)}
                            disabled={platformLoading}
                            className={styles.secondaryBtn}
                          >
                            {platformLoading
                              ? '...'
                              : (language === 'tr' ? 'Kaydet' : 'Save')}
                          </button>
                        </div>
                        <p className={styles.helpText}>
                          {language === 'tr'
                            ? 'Telefon numarası doğrulama gerektirmez'
                            : 'Phone number does not require verification'}
                        </p>
                      </div>

                      <button
                        onClick={cancelEditingPlatform}
                        className={styles.cancelBtn}
                      >
                        {language === 'tr' ? 'İptal' : 'Cancel'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
